/* eslint-disable jsx-a11y/anchor-is-valid */
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Button,
  DrawerBody,
  DrawerFooter,
  DropdownField,
  FormStatusMessage,
  Link,
  LoadingButton,
  MenuItem,
  TextField,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useLazyDataModel } from '@thd-nucleus/data-sources';
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Controller,
  FieldError,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import * as yup from 'yup';
import { useInstantCheckoutStatus } from '../../../../../hooks/use-instant-checkout-status';
import { track } from '../../../../../analytics/analytics';
import {
  INSTANT_CHECKOUT_BUTTON_SUBMISSION,
  INSTANT_CHECKOUT_SUCCESS,
  POST_PAGE_ERROR,
} from '../../../../../constants/analytics';
import {
  ERR_GENERIC,
  ERR_INVALID_PAYMENT,
  ERR_RECAPTCHA,
  ERR_REDIRECT,
} from '../../../../../constants/response-error-messages';
import {
  IPaymentInput,
  TICPaymentFormValues,
} from '../../../../../models/payments';
import {
  expirationMonthValues,
  expirationYearValues,
  getSiteKey,
} from '../../../../../utils/credit-cards';
import { icEditPaymentRule } from '../../../../../utils/validation-rules/instant-checkout-rule';
import { AddressDropdownSelector } from '../../../../shared/address-dropdown';
import { SkeletonLoader } from '../../../../shared/SkeletonLoader';
import { TAlertStatus, TAlertType } from '../../../../../models/common/alert';
import { INSTANT_CHECKOUT_PAYMENT_UPDATE_SUCCESS } from '../../../../../constants/response-success-messages';
import { useDrawer } from '../../../../../hooks/use-drawer';
import {
  addAddresses,
  orangePayToken,
  updateInstantCheckoutSettings,
} from '../../../../../models/graphQLModels';
import { usePayments } from '../../../../../hooks/use-payments';
import { TOrangePaySuccessResponse } from '../../../../../utils/orangePay';
import { shouldShowCardExpirationWithBrand } from '../../../../../utils/payments';
import {
  getChannelID,
  getCustomerIp,
  redirectToSignIn,
} from '../../../../../utils/general';
import { IAddress } from '../../../../../models/address';
import { useAddress } from '../../../../../hooks/use-address';
import { SelectAddressView } from '../../Addresses/SelectAddressView/SelectAddressView';
import { AddAddressView } from '../../Addresses/InstantCheckoutBillingDelivery/AddAddressView';
import {
  getIsAuthError,
  isInvalidPaymentAuthAttempt,
} from '../../../../../constants/error-codes';
import { EDIT_PAYMENT } from '../../../../../models/views';
import {
  createNewRelicActions,
  logNewRelicAction,
} from '../../../../../utils/newrelic';
import { InstantCheckoutDrawerHeader } from '../../../InstantCheckoutDrawerHeader';
import { getCookie } from '../../../../../utils/cookies';

const OrangePayCVVOnlyDynamicImport = lazy(
  () => import('../../../../shared/OrangePayContent'),
);

export const EditPaymentView = () => {
  const cardExpiryMonthRef = useRef(null);
  const cardExpiryYearRef = useRef(null);
  const ReCAPTCHARef = useRef<ReCAPTCHA>(null);
  const [loading, setLoading] = useState(false);
  const [showRecaptchaError, setShowRecaptchaError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [isMicroUILoading, setIsMicroUILoading] = useState(true);
  const [orangePaySuccessData, setOrangePaySuccessData] =
    useState<TOrangePaySuccessResponse | null>(null);
  const [sendOrangePayRemoteAuth, setSendOrangePayRemoteAuth] = useState(false);
  const [isMicroUIFormValid, setIsMicroUIFormValid] = useState(false);
  const [isOrangePayLoaded, setIsOrangePayLoaded] = useState(false);
  const {
    setAlertMessage,
    userId,
    customerAccountId,
    profile,
    refetchProfile,
  } = useInstantCheckoutStatus();
  const { defaultPayment, selectedPaymentMethod, refetchPayments } =
    usePayments();
  const { addresses, selectedAddress, defaultAddress, refetchAddresses } =
    useAddress();
  const [loadAddBilling, setLoadAddBilling] = useState(false);
  const [loadSelectBilling, setLoadSelectBilling] = useState(false);
  const { hideDrawer } = useDrawer();
  const actions = createNewRelicActions('EditPaymentView');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const assignErrorMessage = (message: string | null) => {
    setShowRecaptchaError(false);
    setErrorMessage(message);
    if (message) {
      resetReCaptcha();
    }
  };

  const validationSchema = yup.object().shape(icEditPaymentRule);

  const paymentEdited = selectedPaymentMethod || defaultPayment;

  const formMethods = useForm<TICPaymentFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      cardNickName: paymentEdited?.nickName || '',
      cardHolderName: paymentEdited?.cardDetails?.cardHolderName,
      expirationMonth: paymentEdited?.cardDetails?.expirationMonth || '',
      expirationYear: paymentEdited?.cardDetails?.expirationYear || '',
      billingAddressID: paymentEdited?.billingAddress?.id,
      showExpiration: shouldShowCardExpirationWithBrand(
        paymentEdited?.cardDetails?.cardBrand?.name,
      ),
      recaptcha: '',
      cardNumber: paymentEdited?.cardDetails?.cardNumberLast4,
      cardBrand: paymentEdited?.cardDetails?.cardBrand?.name,
    },
    mode: 'onTouched',
    context: { isEditMode: true },
  });
  const {
    control,
    formState: { errors: formErrors, defaultValues, isValid },
    watch,
    trigger,
    resetField,
    getValues,
  } = formMethods;

  const showExpiration = watch('showExpiration');
  const [month, year] = watch(['expirationMonth', 'expirationYear']);

  const [callUpdateInstantCheckout, updateInstantCheckoutData] =
    useLazyDataModel('updateInstantCheckoutSettings');

  const handleAddAPIError = (errors: []) => {
    if (isInvalidPaymentAuthAttempt(errors)) {
      assignErrorMessage(ERR_INVALID_PAYMENT.message);
    } else {
      assignErrorMessage(ERR_GENERIC.message);
    }
  };

  const {
    called: updateInstantCheckoutCalled,
    loading: updateInstantCheckoutLoading,
    error: updateInstantCheckoutError,
    data: updateInstantCheckoutResult,
  } = updateInstantCheckoutData;

  useEffect(() => {
    if (updateInstantCheckoutError) {
      logNewRelicAction(actions.EDIT_PAYMENT, updateInstantCheckoutError);
      if (getIsAuthError(updateInstantCheckoutError)) {
        setAlertMessage({
          alertType: TAlertType.GENERAL,
          alertStatus: TAlertStatus.INFO,
          text: ERR_REDIRECT.message,
        });
        redirectToSignIn();
      }
      if (
        updateInstantCheckoutError?.graphQLErrors &&
        Array.isArray(updateInstantCheckoutError.graphQLErrors)
      ) {
        handleAddAPIError(updateInstantCheckoutError.graphQLErrors);
      } else {
        assignErrorMessage(ERR_GENERIC.message);
      }
      setLoading(false);
      track(POST_PAGE_ERROR, {
        pageSection: 'edit payment overlay',
        message: ERR_GENERIC.message,
        code: updateInstantCheckoutError?.errorCode || 'n/a',
        type: 'error',
        displayType: 'inline',
      });
    }

    if (
      updateInstantCheckoutResult &&
      updateInstantCheckoutCalled &&
      !updateInstantCheckoutLoading &&
      !updateInstantCheckoutError
    ) {
      logNewRelicAction(actions.EDIT_PAYMENT);
      track(INSTANT_CHECKOUT_SUCCESS, {
        action: 'edit',
        item: 'card details',
      });
      setLoading(false);
      setAlertMessage({
        alertType: TAlertType.IC_PAYMENT_UPDATE,
        alertStatus: TAlertStatus.SUCCESS,
        text: INSTANT_CHECKOUT_PAYMENT_UPDATE_SUCCESS.message,
      });
      refetchAddresses();
      refetchPayments();
      refetchProfile();
      hideDrawer();
    }
  }, [
    updateInstantCheckoutError,
    updateInstantCheckoutCalled,
    updateInstantCheckoutLoading,
    updateInstantCheckoutError,
  ]);

  const resetReCaptcha = () => {
    if (ReCAPTCHARef?.current) {
      resetField('recaptcha');
      ReCAPTCHARef.current.reset();
    }
  };

  const onReCaptchaError = () => {
    assignErrorMessage(null);
    setShowRecaptchaError(true);
  };

  const getTextFieldStatus = (textFieldError: FieldError | undefined) =>
    textFieldError ? 'error' : undefined;

  const onSubmit: SubmitHandler<TICPaymentFormValues> = async () => {
    if (!isOrangePayLoaded) {
      return;
    }
    track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
      overlayType: 'edit card details',
      action: 'save changes',
    });
    if (!(isValid === false && isMicroUIFormValid === true)) {
      setSendOrangePayRemoteAuth(true);
      setLoading(true);
    }
  };

  const handleCancel = () => {
    hideDrawer();
    track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
      overlayType: 'edit card details',
      action: 'cancel',
    });
  };

  useEffect(() => {
    if (orangePaySuccessData) {
      const {
        billingAddressID,
        cardHolderName,
        cardNickName,
        expirationMonth,
        expirationYear,
      } = getValues();
      const billingAddress = addresses.find(
        (address: IAddress) => address.id === billingAddressID,
      );

      if (!paymentEdited || !billingAddress) {
        setLoading(false);
        assignErrorMessage(ERR_GENERIC.message);
        return;
      }

      const payment: IPaymentInput = {
        id: paymentEdited.id,
        isDefault: paymentEdited.isDefault,
        isInstantCheckoutDefault: true,
        nickName: cardNickName || null,
        customerIP: getCustomerIp(),
        forterToken: getCookie('forterToken') || '',
        type: 'CREDIT_CARD',
        billingAddress,
        cardDetails: {
          cardBrand: {
            ...paymentEdited.cardDetails.cardBrand,
            paymentMethodCode: orangePaySuccessData.paymentMethodCode,
            reportPaymentMethodCode:
              orangePaySuccessData.reportPaymentMethodCode,
          },
          cardHolderName,

          cardNumberLast4: paymentEdited.cardDetails.cardNumberLast4,
          ...orangePaySuccessData?.cardDetails,
          expirationMonth,
          expirationYear,
        },
        cardReference: orangePaySuccessData?.cardReference,
      };

      // we don't send expiration details with cards that don't have expiration dates (HD Consumer, HD Commerical, PROX)

      if (!showExpiration) {
        delete payment.cardDetails.expirationMonth;
        delete payment.cardDetails.expirationYear;
      }

      delete payment.cardDetails.authorizedBuyerID;
      delete payment.cardDetails.reportPaymentMethodCode;
      delete payment.cardDetails.paymentMethodCode;

      callUpdateInstantCheckout({
        variables: {
          userId,
          customerAccountId,
          channelId: getChannelID(),
          address: selectedAddress || defaultAddress,
          paymentCard: payment,
          account: profile,
        },
        ssr: false,
        context: {
          withAuth: true,
        },
      });
    }
  }, [orangePaySuccessData]);

  const handleSuccess = (cardData: TOrangePaySuccessResponse) => {
    setOrangePaySuccessData(cardData);
  };
  const viewAllClick = () => {
    setLoadSelectBilling(true);
  };

  const addBillingClick = () => {
    setLoadAddBilling(true);
  };

  const customGoBack = () => {
    // if both add and select are true, then the user clicked on the add button on the select Billing Address View
    if (loadAddBilling && loadSelectBilling) {
      setLoadAddBilling(false);
    } else {
      setLoadAddBilling(false);
      setLoadSelectBilling(false);
    }
  };

  return (
    <div>
      {isMicroUILoading ? <SkeletonLoader /> : null}
      <div hidden={isMicroUILoading}>
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(onSubmit)}>
            {loadAddBilling || loadSelectBilling ? (
              <>
                {loadSelectBilling && !loadAddBilling && (
                  <SelectAddressView
                    addressType="Billing"
                    setLoadSelectBilling={setLoadSelectBilling}
                    setLoadAddBilling={setLoadAddBilling}
                    customGoBack={customGoBack}
                  />
                )}
                {loadAddBilling && (
                  <AddAddressView
                    setLoadAddBilling={setLoadAddBilling}
                    addressType="billing"
                    customGoBack={customGoBack}
                    setLoadSelectBilling={setLoadSelectBilling}
                  />
                )}
              </>
            ) : (
              <>
                <InstantCheckoutDrawerHeader
                  title="Edit Card Details"
                  onClose={handleCancel}
                />
                <DrawerBody>
                  <div className={`sui-grid sui-gap-y-6 sui-mb-10 `}>
                    <Controller
                      name="cardNickName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label="Card Nickname"
                          status={getTextFieldStatus(formErrors.cardNickName)}
                          statusMessage={formErrors.cardNickName?.message}
                          InputProps={{ ...field }}
                          optional
                        />
                      )}
                    />

                    <TextField
                      disabled
                      defaultValue={`****${defaultValues?.cardNumber}`}
                      label="Card Number"
                      InputProps={{
                        inputProps: { 'data-qm-mask': '' },
                      }}
                    />
                    <Controller
                      name="cardHolderName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          label="Name on Card"
                          status={getTextFieldStatus(formErrors.cardHolderName)}
                          statusMessage={formErrors.cardHolderName?.message}
                          InputProps={{
                            ...field,
                            inputProps: { 'data-qm-mask': '' },
                          }}
                          required
                        />
                      )}
                    />

                    {showExpiration && (
                      <div className="sui-grid sui-grid-cols-1 sm:sui-grid-cols-2 sui-gap-4 sm:sui-gap-y-0">
                        <div ref={cardExpiryMonthRef}>
                          <Controller
                            name="expirationMonth"
                            control={control}
                            render={({ field }) => {
                              const {
                                ref,
                                value: fieldValue,
                                onChange,
                                ...rest
                              } = field;

                              const onChangeRevised = (
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                onChange(event);
                                if (year) {
                                  trigger('expirationYear');
                                }
                              };
                              return (
                                <DropdownField
                                  fullWidth
                                  label="Expiration Month *"
                                  value={fieldValue}
                                  id="expirationMonth"
                                  onChange={onChangeRevised}
                                  required
                                  status={getTextFieldStatus(
                                    formErrors.expirationMonth,
                                  )}
                                  statusMessage={
                                    formErrors.expirationMonth?.message
                                  }
                                  dropdownProps={{
                                    'data-qm-mask': '',
                                    ...rest,
                                  }}
                                >
                                  {expirationMonthValues.map(
                                    ({ value: itemValue, label }) => (
                                      <MenuItem
                                        key={itemValue}
                                        value={itemValue}
                                      >
                                        {label}
                                      </MenuItem>
                                    ),
                                  )}
                                </DropdownField>
                              );
                            }}
                          />
                        </div>
                        <div ref={cardExpiryYearRef}>
                          <Controller
                            name="expirationYear"
                            control={control}
                            render={({ field }) => {
                              const {
                                ref,
                                value: fieldValue,
                                onChange,
                                ...rest
                              } = field;
                              const onChangeRevised = (
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                onChange(event);
                                if (month) {
                                  trigger('expirationMonth');
                                }
                              };
                              return (
                                <DropdownField
                                  fullWidth
                                  label="Expiration Year *"
                                  value={fieldValue}
                                  id="expirationYear"
                                  onChange={onChangeRevised}
                                  required
                                  status={getTextFieldStatus(
                                    formErrors.expirationYear,
                                  )}
                                  statusMessage={
                                    formErrors.expirationYear?.message
                                  }
                                  dropdownProps={{
                                    'data-qm-mask': '',
                                    ...rest,
                                  }}
                                >
                                  {expirationYearValues.map(
                                    ({ value, label }) => (
                                      <MenuItem key={value} value={value}>
                                        {label}
                                      </MenuItem>
                                    ),
                                  )}
                                </DropdownField>
                              );
                            }}
                          />
                        </div>
                      </div>
                    )}
                    <div>
                      {typeof window !== 'undefined' ? (
                        <Suspense fallback={<div />}>
                          <OrangePayCVVOnlyDynamicImport
                            setIsMicroUILoading={setIsMicroUILoading}
                            isMicroUILoading={isMicroUILoading}
                            assignErrorMessage={assignErrorMessage}
                            setLoading={setLoading}
                            sendOrangePayRemoteAuth={sendOrangePayRemoteAuth}
                            setSendOrangePayRemoteAuth={
                              setSendOrangePayRemoteAuth
                            }
                            setIsMicroUIFormValid={setIsMicroUIFormValid}
                            isCVVOnly
                            setIsOrangePayLoaded={setIsOrangePayLoaded}
                            isOrangePayLoaded={isOrangePayLoaded}
                            successHandler={handleSuccess}
                            card={paymentEdited}
                            view={EDIT_PAYMENT}
                          />
                        </Suspense>
                      ) : (
                        <div />
                      )}
                    </div>
                    <Controller
                      name="billingAddressID"
                      control={control}
                      render={({ field }) => {
                        const {
                          value: fieldValue,
                          onChange,
                          ref,
                          ...rest
                        } = field;
                        return (
                          <AddressDropdownSelector
                            selectedAddrID={fieldValue}
                            statusType={getTextFieldStatus(
                              formErrors.billingAddressID,
                            )}
                            statusMessage={formErrors.billingAddressID?.message}
                            onChange={onChange}
                            {...rest}
                          />
                        );
                      }}
                    />
                    <div className="sui-grid sui-grid-cols-2 sui-gap-32 sm:sui-gap-x-32">
                      <Link
                        align="left"
                        component="button"
                        underline="always"
                        onClick={viewAllClick}
                      >
                        View All
                      </Link>
                      <Link
                        align="right"
                        component="button"
                        underline="always"
                        onClick={addBillingClick}
                      >
                        + Add Address
                      </Link>
                    </div>
                    <div className="sui-mt-10">
                      <Controller
                        name="recaptcha"
                        control={control}
                        render={({ field: { onChange } }) => (
                          <ReCAPTCHA
                            ref={ReCAPTCHARef}
                            sitekey={getSiteKey()}
                            size={isMobileOnly ? 'compact' : 'normal'}
                            onChange={onChange}
                            onErrored={onReCaptchaError}
                          />
                        )}
                      />
                      <FormStatusMessage status="error">
                        {formErrors.recaptcha?.message}
                      </FormStatusMessage>
                    </div>
                    {showRecaptchaError && (
                      <div className="sui-mt-10">
                        <Alert status="error">{ERR_RECAPTCHA.message}</Alert>
                      </div>
                    )}
                    {errorMessage &&
                      (errorMessage === ERR_INVALID_PAYMENT.message ? (
                        <Alert status="error">
                          <Typography>
                            Something went wrong. Correct one or more of these
                            possible issues:
                          </Typography>
                          <Typography component="div">
                            <ul className="sui-mt-4 sui-list-disc sui-list-outside sui-pl-7 ">
                              <li>Card details are not accurate</li>
                              <li>Card was declined by bank/card user</li>
                            </ul>
                          </Typography>
                        </Alert>
                      ) : (
                        <div className="sui-mt-10">
                          <Alert status="error">{errorMessage}</Alert>
                        </div>
                      ))}
                  </div>
                </DrawerBody>
                <DrawerFooter>
                  <div className="sui-flex sui-gap-2">
                    <Button
                      fullWidth
                      variant="secondary"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                    <LoadingButton
                      loading={loading}
                      variant="primary"
                      type="submit"
                      fullWidth
                    >
                      Save Changes
                    </LoadingButton>
                  </div>
                </DrawerFooter>
              </>
            )}
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

EditPaymentView.dataModel = {
  orangePayToken,
  updateInstantCheckoutSettings,
  addAddresses,
};
